import React, { useState } from "react"
import { Col, Form, InputGroup, Row } from "react-bootstrap"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import { useMutation, useQuery } from "react-query"
import { Redirect } from "react-router"
import EventBus from "../../common/EventBus"
import ActionButton from "../../common/button/actionbutton.component"
import SideBar from "../../common/layout/sidebar.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import Configuration3DS from "../../state/configuration3ds/Configuration3DS"
import Configuration3DSService, { UpdateConfiguration3DSPayload } from "../../state/configuration3ds/Configuration3DSService"
import IntlProps from "../../state/helpers/IntlProps"
import PaymentProcessingTabs from "../PaymentProcessingTabs"
import { useGetPaymentAccounts } from "../usePaymentAccountSession"

export enum Strategy3DSOption {
    ALWAYS = "always",
    NEVER = "never",
    OVER_SPECIFIC_AMOUNT = "over-specific-amount",
}

const MESSAGES = defineMessages({
    [Strategy3DSOption.ALWAYS]: {
        id: "payment_processing.configuration_3ds_strategy.always.label",
        defaultMessage: "Always"
    },
    [Strategy3DSOption.NEVER]: {
        id: "payment_processing.configuration_3ds_strategy.never.label",
        defaultMessage: "Never"
    },
    [Strategy3DSOption.OVER_SPECIFIC_AMOUNT]: {
        id: "payment_processing.configuration_3ds_strategy.over_specific_amount.label",
        defaultMessage: "Over a specific amount"
    },
})

const Configuration3DSSetup = (props: IntlProps) => {
    SideBar.activateMenu("payment-processing")

    const [formData, setFormData] = useState<UpdateConfiguration3DSPayload>({
        strategy3DSOption: Strategy3DSOption.NEVER,
        minimumAmountThreshold: 0,
    })

    const handleChange = (e) => {
        const { name, type, value } = e.target
        setFormData({
            ...formData,
            [name]: type === "number" ? Number(value) : value
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        updateConfiguration3DS(formData)
    }
    const { isLoading: isLoadingConfig3DS } = useQuery(["getConfiguration3DS"], async () => {
        return Configuration3DSService.get()
    }, {
        onSuccess: (response: Configuration3DS) => {
            setFormData({
                strategy3DSOption: response.getStrategy3DSOption(),
                minimumAmountThreshold: response.getMinimumThresholdAmount(),
            })
        }
    })

    const { mutateAsync: updateConfiguration3DS, isLoading: isUpdatingConfiguration3DS } = useMutation(
        (payload: UpdateConfiguration3DSPayload) => {
            return Configuration3DSService.update(payload)
        },
        {
            onSuccess: () => {
                EventBus.dispatch(EventBus.TopicSendSuccessNotification, {
                    message: <FormattedMessage
                        id='payment_processing.configuration_3ds.update_success.message'
                        defaultMessage="3DS configuration updated successfully"
                    />
                })
            }
        })


    const {
        isLoading: isLoadingPaymentAccounts,
        hasActiveAccount,
    } = useGetPaymentAccounts()

    if (isLoadingPaymentAccounts || isLoadingConfig3DS) {
        return <LoadingSpinner />
    }

    const isSaveButtonEnabled = () => {
        return formData.minimumAmountThreshold >= 0
    }

    return <>
        {
            !hasActiveAccount ?
                <Redirect to={"/payment-processing/accounts"} />
                :
                <>
                    <PaymentProcessingTabs hasActiveAccount={hasActiveAccount} />

                    <div className="d-flex flex-column gap-3">
                        <div className="row">
                            <div className="col">
                                <h2 className="custom-lead pl-2 mb-0">
                                    <FormattedMessage
                                        id="payment_processing.configuration_3ds.header"
                                        defaultMessage="3D Secure"
                                    />
                                </h2>
                            </div>
                        </div>

                        <Form id="3ds-form" onSubmit={handleSubmit} className="ui-module mb-0">
                            <Form.Group as={Row} className="mb-3 p-3" controlId="formstrategy3DSOption">
                                <Form.Label className="text-align-right pt-0" column sm={3}>
                                    <FormattedMessage
                                        id="payment_processing.configuration_3ds_strategy.label"
                                        defaultMessage="Request 3D secure authentication"
                                    />
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Check
                                        type="radio"
                                        label={props.intl.formatMessage(MESSAGES[Strategy3DSOption.ALWAYS])}
                                        name="strategy3DSOption"
                                        value={Strategy3DSOption.ALWAYS}
                                        checked={formData.strategy3DSOption === Strategy3DSOption.ALWAYS}
                                        onChange={handleChange}
                                    />
                                    <Form.Check
                                        type="radio"
                                        label={props.intl.formatMessage(MESSAGES[Strategy3DSOption.NEVER])}
                                        name="strategy3DSOption"
                                        value={Strategy3DSOption.NEVER}
                                        checked={formData.strategy3DSOption === Strategy3DSOption.NEVER}
                                        onChange={handleChange}
                                    />
                                    <Form.Check
                                        type="radio"
                                        label={props.intl.formatMessage(MESSAGES[Strategy3DSOption.OVER_SPECIFIC_AMOUNT])}
                                        name="strategy3DSOption"
                                        value={Strategy3DSOption.OVER_SPECIFIC_AMOUNT}
                                        checked={formData.strategy3DSOption === Strategy3DSOption.OVER_SPECIFIC_AMOUNT}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>

                            {formData.strategy3DSOption === Strategy3DSOption.OVER_SPECIFIC_AMOUNT &&
                                <>
                                    <Form.Group as={Row} className="mb-3 px-3">
                                        <Form.Label className="text-align-right pt-0" column sm={3}>
                                            <FormattedMessage
                                                id="payment_processing.configuration_3ds.amount.label"
                                                defaultMessage="Amount"
                                            />
                                        </Form.Label>
                                        <Col sm={3}>
                                            <InputGroup className="mb-2">
                                                <InputGroup.Text id="minimum-amount-threshold">$</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    value={formData.minimumAmountThreshold}
                                                    name="minimumAmountThreshold"
                                                    onChange={handleChange}
                                                    isInvalid={formData.minimumAmountThreshold < 0}
                                                    aria-label="minimum-amount-threshold"
                                                    aria-describedby="minimum-amount-threshold"
                                                />
                                            </InputGroup>
                                            <div>
                                                <small className="form-text text-muted">
                                                    <FormattedMessage
                                                        id="payment_processing.configuration_3ds.amount.description"
                                                        defaultMessage="Amount over which 3D secure will be requested"
                                                    />
                                                </small>
                                            </div>
                                        </Col>
                                    </Form.Group>
                                </>
                            }
                        </Form>

                        <div className="col-12 ui-module">
                            <div className="row">
                                <div className="col-12 text-align-right">
                                    <ActionButton
                                        type="button"
                                        onClick={handleSubmit}
                                        className="btn btn-primary"
                                        isLoading={isUpdatingConfiguration3DS}
                                        disabled={!isSaveButtonEnabled()}
                                    >
                                        <FormattedMessage
                                            id="common.button.save"
                                            defaultMessage="Save"
                                        />
                                    </ActionButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
        }
    </>
}
export default injectIntl(Configuration3DSSetup)


// WEBPACK FOOTER //
// ./src/payment-processing/configuration-3ds/Configuration3DSSetup.tsx